import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { buildAdjustmentsView, type AdjustmentsView } from './adjustmentsModel';
import type {
  AuditLogEntry,
  BalanceLedgerEntry,
  User,
} from '../dataProvider/types';

/**
 * The Manual balance adjustments ledger, read through the aggregation seam
 * (`buildAdjustmentsView`), NOT computed in the component. For v1 it pulls the raw
 * shared ledger, users and audit trail from the mock provider and aggregates client-
 * side; the real-data migration replaces the fetch-and-aggregate here with a server-
 * side endpoint and the surface does not change. Same seam idiom as useChargebacks.
 *
 * OPERATIONAL, SWITCHER-SCOPED (D-balance-adjustment): scope comes from the region
 * context (UK | US | GLOBAL), exactly like the chargebacks and cashout queues. A
 * single-region super_admin sees only their market's adjustments.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useAdjustments = (): {
  view: AdjustmentsView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['adjustments', region],
    queryFn: async () => {
      const [ledger, users, audit] = await Promise.all([
        dataProvider.getList('balanceLedger', ALL),
        dataProvider.getList('users', ALL),
        dataProvider.getList('audit', {
          ...ALL,
          filter: { resource: 'balanceLedger' },
        }),
      ]);
      return buildAdjustmentsView(
        {
          ledger: ledger.data as unknown as BalanceLedgerEntry[],
          users: users.data as unknown as User[],
          audit: audit.data as unknown as AuditLogEntry[],
        },
        { scope: region },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
